import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Edit,
  UserPlus,
  UserMinus,
  Settings,
  User,
  Trash2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface UserProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  email: string | null;
  role: string;
  suspended: boolean;
  last_login: string | null;
  assigned_clients: string[] | null;
  assigned_projects: string[] | null;
  profile_image_url: string | null;
  created_at: string;
  manager_id: string | null;
  cost_per_hour: number | null;
  manager?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    full_name: string | null;
  } | null;
}

interface UserPermission {
  id: string;
  user_id: string;
  module: string;
  permission_level: string;
}

interface UserTableProps {
  users: UserProfile[];
  onEditUser: (user: UserProfile) => void;
  onSuspendToggle: (user: UserProfile) => void;
  onDeleteUser: (user: UserProfile) => void;
  onManagePermissions: (user: UserProfile) => void;
}

const getSuspendedColor = (suspended: boolean) => {
  return suspended ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800";
};

const formatLastLogin = (lastLogin: string | null) => {
  if (!lastLogin) return "Never";
  
  const date = new Date(lastLogin);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "1 day ago";
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return date.toLocaleDateString();
};

export function UserTable({ users, onEditUser, onSuspendToggle, onDeleteUser, onManagePermissions }: UserTableProps) {
  const [userPermissions, setUserPermissions] = useState<Record<string, UserPermission[]>>({});

  useEffect(() => {
    fetchUserPermissions();
  }, [users]);

  const fetchUserPermissions = async () => {
    try {
      const userIds = users.map(user => user.user_id);
      if (userIds.length === 0) return;

      const { data, error } = await supabase
        .from('user_permissions')
        .select('*')
        .in('user_id', userIds);

      if (error) {
        console.error('Error fetching permissions:', error);
        return;
      }

      // Group permissions by user_id
      const permissionsByUser = (data || []).reduce((acc, permission) => {
        if (!acc[permission.user_id]) {
          acc[permission.user_id] = [];
        }
        acc[permission.user_id].push(permission);
        return acc;
      }, {} as Record<string, UserPermission[]>);

      setUserPermissions(permissionsByUser);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const getPermissionsBadge = (user: UserProfile) => {
    const permissions = userPermissions[user.user_id] || [];
    
    // Check if user is stamatis or lefki - super admin
    const isSuperAdmin = user.email?.toLowerCase().includes('stamatis') || 
                        user.first_name?.toLowerCase() === 'stamatis' ||
                        user.email?.toLowerCase().includes('lefki') ||
                        user.first_name?.toLowerCase() === 'lefki';
    
    if (isSuperAdmin) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge className="bg-purple-100 text-purple-800">Super Admin</Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Super Admin has access to all tabs</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // If user has admin role but no explicit permissions, show as having access to all tabs
    if (user.role === 'admin' && permissions.length === 0) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge className="bg-green-100 text-green-800">11 Tabs</Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Admin has access to all tabs</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (permissions.length === 0) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge className="bg-gray-100 text-gray-800">No Access</Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>No tab access assigned</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    const tabCount = new Set(permissions.map(p => p.module)).size;
    const hasFullAccess = permissions.some(p => p.permission_level === 'full');
    
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge className={hasFullAccess ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>
              {tabCount} Tabs
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1">
              <p className="font-semibold">Tab Access:</p>
              {permissions.map(permission => (
                <div key={permission.id} className="text-sm">
                  <span className="capitalize">{permission.module}</span>: {permission.permission_level}
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  if (users.length === 0) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="text-center">
            <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No users found</h3>
            <p className="text-muted-foreground">Try adjusting your filters or search term.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Tab Access</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
          {users.map((user) => {
              return (
                <TableRow key={user.id} className="cursor-pointer hover:bg-muted/50">
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.profile_image_url || ""} />
                        <AvatarFallback>
                          {user.first_name?.[0]}{user.last_name?.[0]} {user.email?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name}` 
                          : user.full_name || 'Unknown User'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getSuspendedColor(user.suspended)}>
                      {user.suspended ? "Suspended" : "Active"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {getPermissionsBadge(user)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onManagePermissions(user)}
                        title="Manage Tab Access"
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onEditUser(user)}
                        title="Edit User"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onSuspendToggle(user)}
                        title={user.suspended ? "Unsuspend User" : "Suspend User"}
                      >
                        {user.suspended ? (
                          <UserPlus className="h-4 w-4 text-green-600" />
                        ) : (
                          <UserMinus className="h-4 w-4 text-red-600" />
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onDeleteUser(user)}
                        title="Delete User"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}